// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import { CallWithChatAdapter } from './CallWithChatAdapter';
import { CallAdapter, CallAdapterState } from '../../CallComposite';
/* @conditional-compile-remove(video-background-effects) */
import { VideoBackgroundImage, VideoBackgroundEffect } from '../../CallComposite';
import { CreateVideoStreamViewResult, VideoStreamOptions } from '@internal/react-components';
import { AudioDeviceInfo, VideoDeviceInfo, Call, PermissionConstraints, StartCallOptions } from '@azure/communication-calling';
/* @conditional-compile-remove(close-captions) */
import { StartCaptionsOptions } from '@azure/communication-calling';
import { CallWithChatAdapterState } from '../state/CallWithChatAdapterState';
import { _toCommunicationIdentifier } from '@internal/acs-ui-common';
import { JoinCallOptions } from '../../CallComposite/adapter/CallAdapter';

/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */

/**
 * Facade around the CallWithChatAdapter to satisfy the call adapter interface.
 *
 * @private
 */
export class CallWithChatBackedCallAdapter implements CallAdapter {
  private callWithChatAdapter: CallWithChatAdapter;

  // For onStateChange we must convert CallWithChat state to chat state. This involves creating a new handler to be passed into the onStateChange.
  // In order to unsubscribe the handler when offStateChange is called we must have a mapping of the original handler to the newly created handler.
  private eventStore: Map<(state: CallAdapterState) => void, (state: CallWithChatAdapterState) => void> = new Map();
  constructor(callWithChatAdapter: CallWithChatAdapter) {
    this.callWithChatAdapter = callWithChatAdapter;
  }
  public on = (event: any, listener: any): void => {
    switch (event) {
      case 'error':
        return this.callWithChatAdapter.on('callError', listener);
      case 'participantsJoined':
        return this.callWithChatAdapter.on('callParticipantsJoined', listener);
      case 'participantsLeft':
        return this.callWithChatAdapter.on('callParticipantsLeft', listener);
      default:
        return this.callWithChatAdapter.on(event, listener);
    }
  };
  public off = (event: any, listener: any): void => {
    switch (event) {
      case 'error':
        return this.callWithChatAdapter.off('callError', listener);
      case 'participantsJoined':
        return this.callWithChatAdapter.off('callParticipantsJoined', listener);
      case 'participantsLeft':
        return this.callWithChatAdapter.off('callParticipantsLeft', listener);
      default:
        return this.callWithChatAdapter.off(event, listener);
    }
  };
  public onStateChange = (handler: (state: CallAdapterState) => void): void => {
    const convertedHandler = (state: CallWithChatAdapterState): void => {
      handler(callAdapterStateFromCallWithChatAdapterState(state));
    };
    this.callWithChatAdapter.onStateChange(convertedHandler);
    this.eventStore.set(handler, convertedHandler);
  };
  public offStateChange = (handler: (state: CallAdapterState) => void): void => {
    const convertedHandler = this.eventStore.get(handler);
    convertedHandler && this.callWithChatAdapter.offStateChange(convertedHandler);
  };
  public getState = (): CallAdapterState => callAdapterStateFromCallWithChatAdapterState(this.callWithChatAdapter.getState());
  public dispose = (): void => this.callWithChatAdapter.dispose();
  public joinCall = (options?: boolean | JoinCallOptions): Call | undefined => {
    if (typeof options === 'boolean') {
      return this.callWithChatAdapter.joinCall(options);
    } else {
      return this.callWithChatAdapter.joinCall(options);
    }
  };
  public leaveCall = async (forEveryone?: boolean): Promise<void> => await this.callWithChatAdapter.leaveCall(forEveryone);
  public startCall = (participants: string[], options: StartCallOptions): Call | undefined => {
    let communicationParticipants = participants;
    return this.callWithChatAdapter.startCall(communicationParticipants, options);
  };
  public setCamera = async (sourceId: VideoDeviceInfo, options?: VideoStreamOptions): Promise<void> => await this.callWithChatAdapter.setCamera(sourceId, options);
  public setMicrophone = async (sourceId: AudioDeviceInfo): Promise<void> => await this.callWithChatAdapter.setMicrophone(sourceId);
  public setSpeaker = async (sourceId: AudioDeviceInfo): Promise<void> => await this.callWithChatAdapter.setSpeaker(sourceId);
  public askDevicePermission = async (constraints: PermissionConstraints): Promise<void> => await this.callWithChatAdapter.askDevicePermission(constraints);
  public queryCameras = async (): Promise<VideoDeviceInfo[]> => await this.callWithChatAdapter.queryCameras();
  public queryMicrophones = async (): Promise<AudioDeviceInfo[]> => await this.callWithChatAdapter.queryMicrophones();
  public querySpeakers = async (): Promise<AudioDeviceInfo[]> => await this.callWithChatAdapter.querySpeakers();
  public startCamera = async (options?: VideoStreamOptions): Promise<void> => await this.callWithChatAdapter.startCamera(options);
  public stopCamera = async (): Promise<void> => await this.callWithChatAdapter.stopCamera();
  public mute = async (): Promise<void> => await this.callWithChatAdapter.mute();
  public unmute = async (): Promise<void> => await this.callWithChatAdapter.unmute();
  public startScreenShare = async (): Promise<void> => await this.callWithChatAdapter.startScreenShare();
  public stopScreenShare = async (): Promise<void> => await this.callWithChatAdapter.stopScreenShare();
  /* @conditional-compile-remove(raise-hand) */
  public raiseHand = async (): Promise<void> => await this.callWithChatAdapter.raiseHand();
  /* @conditional-compile-remove(raise-hand) */
  public lowerHand = async (): Promise<void> => await this.callWithChatAdapter.lowerHand();
  public removeParticipant = async (userId: string): Promise<void> => {
    let participant = userId;
    await this.callWithChatAdapter.removeParticipant(participant);
  };
  public createStreamView = async (remoteUserId?: string, options?: VideoStreamOptions): Promise<void | CreateVideoStreamViewResult> => await this.callWithChatAdapter.createStreamView(remoteUserId, options);
  public disposeStreamView = async (remoteUserId?: string, options?: VideoStreamOptions): Promise<void> => await this.callWithChatAdapter.disposeStreamView(remoteUserId, options);
  public disposeScreenShareStreamView(remoteUserId: string): Promise<void> {
    return this.callWithChatAdapter.disposeScreenShareStreamView(remoteUserId);
  }
  public disposeRemoteVideoStreamView(remoteUserId: string): Promise<void> {
    return this.callWithChatAdapter.disposeRemoteVideoStreamView(remoteUserId);
  }
  public disposeLocalVideoStreamView(): Promise<void> {
    return this.callWithChatAdapter.disposeLocalVideoStreamView();
  }
  /* @conditional-compile-remove(close-captions) */
  public async startCaptions(options?: StartCaptionsOptions): Promise<void> {
    this.callWithChatAdapter.startCaptions(options);
  }

  /* @conditional-compile-remove(close-captions) */
  public async stopCaptions(): Promise<void> {
    this.callWithChatAdapter.stopCaptions();
  }

  /* @conditional-compile-remove(close-captions) */
  public async setCaptionLanguage(language: string): Promise<void> {
    this.callWithChatAdapter.setCaptionLanguage(language);
  }

  /* @conditional-compile-remove(close-captions) */
  public async setSpokenLanguage(language: string): Promise<void> {
    this.callWithChatAdapter.setSpokenLanguage(language);
  }

  /* @conditional-compile-remove(video-background-effects) */
  public async startVideoBackgroundEffect(videoBackgroundEffect: VideoBackgroundEffect): Promise<void> {
    await this.callWithChatAdapter.startVideoBackgroundEffect(videoBackgroundEffect);
  }
  /* @conditional-compile-remove(video-background-effects) */
  public async stopVideoBackgroundEffects(): Promise<void> {
    await this.callWithChatAdapter.stopVideoBackgroundEffects();
  }
  /* @conditional-compile-remove(video-background-effects) */
  public updateBackgroundPickerImages(backgroundImages: VideoBackgroundImage[]): void {
    return this.callWithChatAdapter.updateBackgroundPickerImages(backgroundImages);
  }
  /* @conditional-compile-remove(video-background-effects) */
  public updateSelectedVideoBackgroundEffect(selectedVideoBackground: VideoBackgroundEffect): void {
    return this.callWithChatAdapter.updateSelectedVideoBackgroundEffect(selectedVideoBackground);
  }
}
function callAdapterStateFromCallWithChatAdapterState(callWithChatAdapterState: CallWithChatAdapterState): CallAdapterState {
  return {
    isLocalPreviewMicrophoneEnabled: callWithChatAdapterState.isLocalPreviewMicrophoneEnabled,
    page: callWithChatAdapterState.page,
    userId: callWithChatAdapterState.userId,
    displayName: callWithChatAdapterState.displayName,
    call: callWithChatAdapterState.call,
    devices: callWithChatAdapterState.devices,
    isTeamsCall: callWithChatAdapterState.isTeamsCall,
    latestErrors: callWithChatAdapterState.latestCallErrors,
    /* @conditional-compile-remove(video-background-effects) */
    videoBackgroundImages: callWithChatAdapterState.videoBackgroundImages,
    /* @conditional-compile-remove(video-background-effects) */
    onResolveVideoEffectDependency: callWithChatAdapterState.onResolveVideoEffectDependency,
    /* @conditional-compile-remove(video-background-effects) */
    selectedVideoBackgroundEffect: callWithChatAdapterState.selectedVideoBackgroundEffect
  };
}"../../../../../react-components/src""../../../../../acs-ui-common/src"